import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import {
  getCart,
  addToCart as addToCartApi,
  updateCartQty,
  removeFromCart,
  clearCart as clearCartApi,
  setCart as setCartApi,
  getWishlist,
  toggleWishlist as toggleWishlistApi,
  removeFromWishlist as removeFromWishlistApi,
  clearWishlist as clearWishlistApi,
} from "../lib/cartWishlistService";
import { getProductById } from "../lib/productService";

const CartWishlistContext = createContext(null);

const GUEST_CART_KEY = "ayrine_guest_cart";
const GUEST_WISH_KEY = "ayrine_guest_wishlist";

const getGuestCart = () => JSON.parse(localStorage.getItem(GUEST_CART_KEY) || "[]");
const setGuestCart = (cart) => localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));

const getGuestWishlist = () => JSON.parse(localStorage.getItem(GUEST_WISH_KEY) || "[]");
const setGuestWishlist = (wishlist) => localStorage.setItem(GUEST_WISH_KEY, JSON.stringify(wishlist));

export function CartWishlistProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshCart = useCallback(async () => {
    if (!user?.id) {
      setCart(getGuestCart());
      return;
    }
    const items = await getCart(user.id);
    setCart(items);
  }, [user?.id]);

  const refreshWishlist = useCallback(async () => {
    if (!user?.id) {
      setWishlist(getGuestWishlist());
      return;
    }
    const items = await getWishlist(user.id);
    setWishlist(items);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setCart(getGuestCart());
      setWishlist(getGuestWishlist());
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([getCart(user.id), getWishlist(user.id)]).then(([c, w]) => {
      setCart(c);
      setWishlist(w);
      setLoading(false);
    });
  }, [user?.id]);

  useEffect(() => {
    const onCart = () => refreshCart();
    const onWish = () => refreshWishlist();
    window.addEventListener("cart_updated", onCart);
    window.addEventListener("wishlist_updated", onWish);
    return () => {
      window.removeEventListener("cart_updated", onCart);
      window.removeEventListener("wishlist_updated", onWish);
    };
  }, [refreshCart, refreshWishlist]);

  const addToCart = useCallback(
    async (item) => {
      let liveStock = item.stockQuantity;
      if (liveStock === undefined) {
        const liveProd = await getProductById(item.id);
        liveStock = liveProd?.stockQuantity ?? Infinity;
      }
      
      if (liveStock <= 0) {
        alert("Product is out of stock");
        return;
      }

      const currentItem = cart.find(x => x.id === item.id);
      if (currentItem && (currentItem.qty || 1) >= liveStock) {
        alert("Cannot add more than available stock.");
        return;
      }

      if (!user?.id) {
        const guestCart = getGuestCart();
        const existing = guestCart.find((x) => x.id === item.id);
        if (existing) {
          existing.qty = (existing.qty || 1) + 1;
        } else {
          guestCart.push({ ...item, stockQuantity: liveStock, qty: 1 });
        }
        setGuestCart(guestCart);
        refreshCart();
        return;
      }
      await addToCartApi(user.id, { ...item, stockQuantity: liveStock });
      await refreshCart();
    },
    [user?.id, refreshCart, cart]
  );

  const incQty = useCallback(
    async (productId) => {
      const currentItem = cart.find(x => x.id === productId);
      
      let liveStock = currentItem?.stockQuantity;
      if (liveStock === undefined) {
        const liveProd = await getProductById(productId);
        liveStock = liveProd?.stockQuantity ?? Infinity;
      }

      if (currentItem && (currentItem.qty || 1) >= liveStock) {
        alert("Cannot add more than available stock.");
        return;
      }

      if (!user?.id) {
        const guestCart = getGuestCart();
        const existing = guestCart.find((x) => x.id === productId);
        if (existing) {
          existing.qty = (existing.qty || 1) + 1;
          setGuestCart(guestCart);
          refreshCart();
        }
        return;
      }
      await updateCartQty(user.id, productId, 1);
      await refreshCart();
    },
    [user?.id, refreshCart, cart]
  );

  const decQty = useCallback(
    async (productId) => {
      if (!user?.id) {
        const guestCart = getGuestCart();
        const existing = guestCart.find((x) => x.id === productId);
        if (existing) {
          existing.qty = Math.max(0, (existing.qty || 1) - 1);
          const newCart = existing.qty === 0 ? guestCart.filter((x) => x.id !== productId) : guestCart;
          setGuestCart(newCart);
          refreshCart();
        }
        return;
      }
      await updateCartQty(user.id, productId, -1);
      await refreshCart();
    },
    [user?.id, refreshCart]
  );

  const removeFromCartById = useCallback(
    async (productId) => {
      if (!user?.id) {
        const guestCart = getGuestCart();
        setGuestCart(guestCart.filter((x) => x.id !== productId));
        refreshCart();
        return;
      }
      await removeFromCart(user.id, productId);
      await refreshCart();
    },
    [user?.id, refreshCart]
  );

  const clearCart = useCallback(
    async () => {
      if (!user?.id) {
        setGuestCart([]);
        refreshCart();
        return;
      }
      await clearCartApi(user.id);
      await refreshCart();
    },
    [user?.id, refreshCart]
  );

  const saveCart = useCallback(
    async (updater) => {
      if (!user?.id) {
        const next = typeof updater === "function" ? updater(cart) : updater;
        setGuestCart(next);
        refreshCart();
        return;
      }
      const next = typeof updater === "function" ? updater(cart) : updater;
      await setCartApi(user.id, next);
      await refreshCart();
    },
    [user?.id, cart, refreshCart]
  );

  const toggleWish = useCallback(
    async (item) => {
      if (!user?.id) {
        const guestWish = getGuestWishlist();
        const existing = guestWish.find((x) => x.id === item.id);
        if (existing) {
          setGuestWishlist(guestWish.filter((x) => x.id !== item.id));
        } else {
          setGuestWishlist([...guestWish, item]);
        }
        refreshWishlist();
        return;
      }
      await toggleWishlistApi(user.id, item);
      await refreshWishlist();
    },
    [user?.id, refreshWishlist]
  );

  const isWished = useCallback(
    (productId) => wishlist.some((w) => w.id === productId),
    [wishlist]
  );

  const removeFromWishlistById = useCallback(
    async (productId) => {
      if (!user?.id) {
        const guestWish = getGuestWishlist();
        setGuestWishlist(guestWish.filter((x) => x.id !== productId));
        refreshWishlist();
        return;
      }
      await removeFromWishlistApi(user.id, productId);
      await refreshWishlist();
    },
    [user?.id, refreshWishlist]
  );

  const clearWishlist = useCallback(
    async () => {
      if (!user?.id) {
        setGuestWishlist([]);
        refreshWishlist();
        return;
      }
      await clearWishlistApi(user.id);
      await refreshWishlist();
    },
    [user?.id, refreshWishlist]
  );

  const getQty = useCallback(
    (productId) => cart.find((x) => x.id === productId)?.qty || 0,
    [cart]
  );

  const value = {
    cart,
    wishlist,
    loading,
    addToCart,
    incQty,
    decQty,
    removeFromCart: removeFromCartById,
    clearCart,
    saveCart,
    toggleWish,
    isWished,
    removeFromWishlist: removeFromWishlistById,
    clearWishlist,
    getQty,
    refreshCart,
    refreshWishlist,
  };

  return (
    <CartWishlistContext.Provider value={value}>
      {children}
    </CartWishlistContext.Provider>
  );
}

export function useCartWishlist() {
  const ctx = useContext(CartWishlistContext);
  if (!ctx) throw new Error("useCartWishlist must be used within CartWishlistProvider");
  return ctx;
}
